import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { Loader2, Building2, DollarSign, FileText, Wrench, FileSignature, FolderOpen, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const COLORS = ["#0ea5e9", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444", "#6366f1", "#ec4899"];

function KPI({ label, value, sub, icon: Icon, color = "text-sky-600" }) {
  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
        <div className="font-mono text-2xl font-bold text-slate-900">{value}</div>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children, className = "" }) {
  return (
    <Card className={`border-slate-200 ${className}`}>
      <CardHeader className="pb-2"><CardTitle className="font-heading text-base font-bold">{title}</CardTitle></CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function DataTable({ headers, rows }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            {headers.map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wider">{h}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {row.map((cell, j) => <TableCell key={j} className="text-sm">{cell}</TableCell>)}
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={headers.length} className="text-center text-slate-400 py-8">No data available</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function OccupancyReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/reports/occupancy").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>;
  if (!data) return null;

  const statusData = data.by_status.map(s => ({ name: s._id || "unknown", value: s.count }));
  const occupied = data.by_status.find(s => s._id === "occupied")?.count || 0;
  const available = data.by_status.find(s => s._id === "available")?.count || 0;
  const reserved = data.by_status.find(s => s._id === "reserved")?.count || 0;
  const rate = data.total_rooms > 0 ? Math.round(((occupied + reserved) / data.total_rooms) * 100) : 0;

  const catMap = {};
  data.by_category.forEach(({ _id, count }) => {
    const cat = _id.category || "other";
    if (!catMap[cat]) catMap[cat] = { name: cat, available: 0, occupied: 0, reserved: 0, maintenance: 0 };
    catMap[cat][_id.status] = count;
  });

  const floorMap = {};
  data.by_floor.forEach(({ _id, count }) => {
    const fl = `Floor ${_id.floor}`;
    if (!floorMap[fl]) floorMap[fl] = { name: fl, available: 0, occupied: 0, reserved: 0, maintenance: 0 };
    floorMap[fl][_id.status] = count;
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPI label="Total Rooms" value={data.total_rooms} icon={Building2} />
        <KPI label="Occupancy Rate" value={`${rate}%`} sub={`${occupied} occupied + ${reserved} reserved`} icon={TrendingUp} color="text-emerald-600" />
        <KPI label="Available" value={available} icon={Building2} color="text-amber-600" />
        <KPI label="Under Maintenance" value={data.by_status.find(s => s._id === "maintenance")?.count || 0} icon={Wrench} color="text-red-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Room Status Distribution">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Occupancy by Floor">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={Object.values(floorMap)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="occupied" fill="#0ea5e9" name="Occupied" />
              <Bar dataKey="available" fill="#10b981" name="Available" />
              <Bar dataKey="reserved" fill="#f59e0b" name="Reserved" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <ChartCard title="Breakdown by Category">
        <DataTable
          headers={["Category", "Available", "Occupied", "Reserved", "Maintenance", "Total"]}
          rows={Object.values(catMap).map(c => [
            <span className="capitalize font-medium">{c.name}</span>,
            <Badge className="bg-emerald-100 text-emerald-700">{c.available || 0}</Badge>,
            <Badge className="bg-sky-100 text-sky-700">{c.occupied || 0}</Badge>,
            <Badge className="bg-amber-100 text-amber-700">{c.reserved || 0}</Badge>,
            <Badge className="bg-slate-100 text-slate-500">{c.maintenance || 0}</Badge>,
            (c.available || 0) + (c.occupied || 0) + (c.reserved || 0) + (c.maintenance || 0),
          ])}
        />
      </ChartCard>
    </div>
  );
}

function RevenueReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/reports/revenue").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>;
  if (!data) return null;

  const collectionRate = data.total_all > 0 ? Math.round((data.total_collected / data.total_all) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPI label="Total Collected" value={`$${data.total_collected.toLocaleString()}`} icon={DollarSign} color="text-emerald-600" />
        <KPI label="Pending" value={`$${data.total_pending.toLocaleString()}`} icon={DollarSign} color="text-amber-600" />
        <KPI label="Total Invoiced" value={`$${data.total_all.toLocaleString()}`} icon={DollarSign} />
        <KPI label="Collection Rate" value={`${collectionRate}%`} icon={TrendingUp} color={collectionRate > 80 ? "text-emerald-600" : "text-red-500"} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Revenue by Payment Type">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.by_type.map(t => ({ name: t._id, value: t.total }))} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: $${value}`}>
                {data.by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Monthly Revenue (Paid)">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
              <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
              <Bar dataKey="total" fill="#0ea5e9" name="Revenue" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <ChartCard title="Payment Status Breakdown">
        <DataTable
          headers={["Status", "Count", "Total Amount", "% of Total"]}
          rows={data.by_status.map(s => [
            <Badge className={s._id === "paid" ? "bg-emerald-100 text-emerald-700" : s._id === "pending" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}>{s._id}</Badge>,
            s.count,
            `$${s.total.toLocaleString()}`,
            data.total_all > 0 ? `${Math.round(s.total / data.total_all * 100)}%` : "0%",
          ])}
        />
      </ChartCard>
    </div>
  );
}

function ApplicationsReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/reports/applications").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPI label="Total Applications" value={data.total} icon={FileText} />
        <KPI label="Approved" value={data.approved} icon={TrendingUp} color="text-emerald-600" />
        <KPI label="Rejected" value={data.rejected} icon={TrendingDown} color="text-red-500" />
        <KPI label="Conversion Rate" value={`${data.conversion_rate}%`} icon={TrendingUp} color={data.conversion_rate > 50 ? "text-emerald-600" : "text-amber-600"} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Applications by Status">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.by_status.map(s => ({ name: s._id?.replace(/_/g, " "), value: s.count }))} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3} dataKey="value" label>
                {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Applications Over Time">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#f59e0b" name="Applications" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <ChartCard title="Applications by Room Category">
        <DataTable
          headers={["Room Category", "Applications", "% of Total"]}
          rows={data.by_room_category.map(c => [
            <span className="capitalize font-medium">{c._id || "unspecified"}</span>,
            c.count,
            data.total > 0 ? `${Math.round(c.count / data.total * 100)}%` : "0%",
          ])}
        />
      </ChartCard>
    </div>
  );
}

function MaintenanceReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/reports/maintenance").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>;
  if (!data) return null;

  const resolutionRate = data.total > 0 ? Math.round(data.resolved / data.total * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPI label="Total Requests" value={data.total} icon={Wrench} />
        <KPI label="Open" value={data.open} icon={Wrench} color="text-red-500" />
        <KPI label="Resolved" value={data.resolved} icon={Wrench} color="text-emerald-600" />
        <KPI label="Resolution Rate" value={`${resolutionRate}%`} icon={TrendingUp} color={resolutionRate > 70 ? "text-emerald-600" : "text-amber-600"} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="By Category">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.by_category} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="_id" type="category" tick={{ fontSize: 11 }} width={80} />
              <Tooltip />
              <Bar dataKey="count" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="By Severity">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.by_severity.map(s => ({ name: s._id, value: s.count }))} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" label>
                {data.by_severity.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="By Status">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.by_status.map(s => ({ name: s._id?.replace(/_/g, " "), value: s.count }))} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" label>
                {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <ChartCard title="Maintenance Over Time">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data.by_month}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="count" fill="#ef4444" name="Requests" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function LeasesReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/reports/leases").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPI label="Total Leases" value={data.total} icon={FileSignature} />
        <KPI label="Active" value={data.active} icon={FileSignature} color="text-emerald-600" />
        <KPI label="Pending Sign" value={data.pending_signatures} icon={FileSignature} color="text-amber-600" />
        <KPI label="Renewals" value={data.renewals_requested} icon={FileSignature} color="text-sky-600" />
        <KPI label="Monthly Rev" value={`$${data.monthly_revenue.toLocaleString()}`} icon={DollarSign} color="text-emerald-600" />
        <KPI label="Deposits Held" value={`$${data.total_deposits.toLocaleString()}`} icon={DollarSign} />
      </div>
      <ChartCard title="Lease Status Distribution">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={data.by_status.map(s => ({ name: s._id?.replace(/_/g, " "), value: s.count }))} cx="50%" cy="50%" innerRadius={70} outerRadius={110} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
              {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function DocumentsReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/reports/documents").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>;
  if (!data) return null;

  const approvalRate = data.total > 0 ? Math.round(data.approved / data.total * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPI label="Total Documents" value={data.total} icon={FolderOpen} />
        <KPI label="Approved" value={data.approved} icon={FolderOpen} color="text-emerald-600" />
        <KPI label="Pending Review" value={data.pending} icon={FolderOpen} color="text-amber-600" />
        <KPI label="Approval Rate" value={`${approvalRate}%`} icon={TrendingUp} color={approvalRate > 70 ? "text-emerald-600" : "text-amber-600"} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Documents by Type">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_type.map(t => ({ name: t._id?.replace(/_/g, " "), count: t.count }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-15} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Review Status">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.by_status.map(s => ({ name: s._id, value: s.count }))} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" label>
                {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

export default function AdminReportsPage() {
  const [activeTab, setActiveTab] = useState("occupancy");

  return (
    <DashboardLayout title="Reports & Analytics">
      <div className="space-y-6" data-testid="admin-reports-page">
        <div>
          <p className="text-sm text-slate-500">Comprehensive reports across all platform functions. Select a report category below.</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-auto flex-wrap bg-white border border-slate-200 p-1">
            <TabsTrigger value="occupancy" className="text-xs data-[state=active]:bg-sky-500 data-[state=active]:text-white" data-testid="tab-occupancy">
              <Building2 className="h-3.5 w-3.5 mr-1.5" /> Occupancy
            </TabsTrigger>
            <TabsTrigger value="revenue" className="text-xs data-[state=active]:bg-sky-500 data-[state=active]:text-white" data-testid="tab-revenue">
              <DollarSign className="h-3.5 w-3.5 mr-1.5" /> Revenue
            </TabsTrigger>
            <TabsTrigger value="applications" className="text-xs data-[state=active]:bg-sky-500 data-[state=active]:text-white" data-testid="tab-applications">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Applications
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="text-xs data-[state=active]:bg-sky-500 data-[state=active]:text-white" data-testid="tab-maintenance">
              <Wrench className="h-3.5 w-3.5 mr-1.5" /> Maintenance
            </TabsTrigger>
            <TabsTrigger value="leases" className="text-xs data-[state=active]:bg-sky-500 data-[state=active]:text-white" data-testid="tab-leases">
              <FileSignature className="h-3.5 w-3.5 mr-1.5" /> Leases
            </TabsTrigger>
            <TabsTrigger value="documents" className="text-xs data-[state=active]:bg-sky-500 data-[state=active]:text-white" data-testid="tab-documents">
              <FolderOpen className="h-3.5 w-3.5 mr-1.5" /> Documents
            </TabsTrigger>
          </TabsList>

          <TabsContent value="occupancy"><OccupancyReport /></TabsContent>
          <TabsContent value="revenue"><RevenueReport /></TabsContent>
          <TabsContent value="applications"><ApplicationsReport /></TabsContent>
          <TabsContent value="maintenance"><MaintenanceReport /></TabsContent>
          <TabsContent value="leases"><LeasesReport /></TabsContent>
          <TabsContent value="documents"><DocumentsReport /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
